import React, { useState } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import Header from '../components/Headers/Header'
import CustomLoader from '../components/Loader/CustomLoader'
import DataTable from '../components/DataTable'
import orderBy from 'lodash/orderBy'
import { getCuisines, editCuisine, deleteCuisine } from '../apollo'
import SearchBar from '../components/TableHeader/SearchBar'
import useGlobalStyles from '../utils/globalStyles'
import { customStyles } from '../utils/tableCustomStyles'
import { RowActionMenu } from '../components/UI/RowActionMenu'
import EditOutlined from '@mui/icons-material/EditOutlined'
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded'
import { Box, Container, Modal, Switch, Button } from '@mui/material'
import AddRounded from '@mui/icons-material/AddRounded'
import { raisedButtonSx } from '../components/UI/Depth'
import TableHeader from '../components/TableHeader'
import CuisineComponent from '../components/Cuisine/Cuisine'
import { modalOverlay, modalCard } from '../utils/modalStyles'

const GET_CUISINES = gql`
  ${getCuisines}
`
const EDIT_CUISINE = gql`
  ${editCuisine}
`
const DELETE_CUISINE = gql`
  ${deleteCuisine}
`

const Cuisines = props => {
  const { t } = props
  const [editModal, setEditModal] = useState(false)
  const [addModal, setAddModal] = useState(false)
  const [cuisine, setCuisine] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const onChangeSearch = e => setSearchQuery(e.target.value)
  const [mutateEdit] = useMutation(EDIT_CUISINE)
  const [mutateDelete] = useMutation(DELETE_CUISINE, {
    refetchQueries: [{ query: GET_CUISINES }]
  })
  const { data, error: errorQuery, loading: loadingQuery, refetch } = useQuery(
    GET_CUISINES
  )
  const toggleModal = cuisine => {
    setEditModal(!editModal)
    setCuisine(cuisine)
  }

  const customSort = (rows, field, direction) => {
    const handleField = row => {
      if (row[field] && isNaN(row[field])) {
        return row[field].toLowerCase()
      }

      return row[field]
    }

    return orderBy(rows, handleField, direction)
  }

  const columns = [
    {
      name: t('Icon'),
      cell: row =>
        row.image ? (
          <img
            src={row.image}
            alt={row.name}
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              objectFit: 'cover'
            }}
          />
        ) : (
          <span style={{ fontSize: 22 }}>{row.emoji || '-'}</span>
        )
    },
    {
      name: t('Name'),
      sortable: true,
      selector: 'name'
    },
    {
      name: t('Description'),
      sortable: true,
      selector: 'description'
    },
    {
      name: t('Action'),
      cell: row => <>{actionButtons(row)}</>
    }
  ]
  const regex =
    searchQuery.length > 2 ? new RegExp(searchQuery.toLowerCase(), 'g') : null
  const filtered =
    searchQuery.length < 3
      ? data && data.cuisines
      : data &&
        data.cuisines.filter(cuisine => {
          return cuisine.name.toLowerCase().search(regex) > -1
        })

  const actionButtons = row => {
    return (
      <>
        <div>
          <RowActionMenu
            label={t('Action')}
            actions={[
              {
                label: t('Edit'),
                icon: EditOutlined,
                tone: 'primary',
                onClick: () => {
                  toggleModal(row)
                }
              },
              {
                label: t('Delete'),
                icon: DeleteOutlineRounded,
                tone: 'danger',
                divider: true,
                confirm: {
                  title: `${t('Delete')}?`,
                  message: t('DeleteConfirm'),
                  cancelLabel: t('Cancel')
                },
                onClick: () => {
                  mutateDelete({ variables: { id: row._id } })
                }
              }
            ]}
          />
        </div>
      </>
    )
  }
  const globalClasses = useGlobalStyles()
  return (
    <>
      <Header
        action={
          <Button
            startIcon={<AddRounded />}
            onClick={() => setAddModal(true)}
            sx={raisedButtonSx}>
            {`${t('Add')} ${t('Cuisine')}`}
          </Button>
        }
      />
      {/* Page content */}
      <Container className={globalClasses.flex} fluid>
        {errorQuery ? (
          <span>
            `${t('Error')}! ${errorQuery.message}`
          </span>
        ) : null}
        {loadingQuery ? (
          <CustomLoader />
        ) : (
          <DataTable
            subHeader={true}
            subHeaderComponent={
              <SearchBar
                value={searchQuery}
                onChange={onChangeSearch}
                onClick={() => refetch()}
              />
            }
            title={<TableHeader title={t('Cuisines')} />}
            columns={columns}
            data={filtered}
            pagination
            progressPending={loadingQuery}
            progressComponent={<CustomLoader />}
            sortFunction={customSort}
            defaultSortField="name"
            customStyles={customStyles}
          />
        )}
        <Modal
          open={editModal}
          onClose={() => {
            toggleModal(null)
          }}
          sx={modalOverlay}>
          <Box sx={modalCard('md')}>
            <CuisineComponent
              cuisine={cuisine}
              onClose={() => toggleModal(null)}
            />
          </Box>
        </Modal>
        <Modal
          open={addModal}
          onClose={() => setAddModal(false)}
          sx={modalOverlay}>
          <Box sx={modalCard('md')}>
            <CuisineComponent onClose={() => setAddModal(false)} />
          </Box>
        </Modal>
      </Container>
    </>
  )
}

export default withTranslation()(Cuisines)
